<div class="card rounded-lg border-0 shadow mt-2">
  <div class="card-body rounded-lg shadow">
  <div class="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3 mb-4">
    <div>
      <p class="text-uppercase text-muted text-xxs mb-1">{{ display_title }}</p>
      <p class="text-x fw-bold mb-1">{{ display_subtitle }}</p>
      <p class="text-muted text-xxs mb-0">
        {{ translate_label("Select an output format, review the template setup, or jump straight into a quick generate.") }}
      </p>
    </div>
    <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
      <select
        v-model="selected_output_format_id"
        class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
        :disabled="loading_formats"
      >
        <option disabled value="">Select Type</option>
        <option v-for="format in output_formats" :key="format.id" :value="format.id">
          {{ format.display_name }}
        </option>
      </select>
      <button
        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
        :disabled="loading_templates"
        type="button"
        @click="load_templates(page_number)"
      >
        Refresh
      </button>
    </div>
  </div>

  <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
    <div class="d-flex align-items-center gap-2">
      <div v-if="searching_templates" class="d-flex align-items-center gap-1 text-muted text-xxs">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span>Searching...</span>
      </div>
    </div>
    <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
      <input
        v-model.trim="search_query"
        class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
        :placeholder="translate_label('Search templates...')"
        type="text"
        @input="schedule_search"
      />
    </div>
  </div>

  <div class="row g-3 mb-4">
    <div v-for="card in summary_cards" :key="card.label" class="col-12 col-md-4">
      <div class="card shadow-sm border rounded-lg p-3 h-100">
        <p class="text-uppercase text-muted text-xxs mb-1">{{ card.label }}</p>
        <p class="fw-bold text-dark text-xxs mb-1">{{ card.value }}</p>
        <p class="text-muted text-xxs mb-0">{{ card.description }}</p>
      </div>
    </div>
  </div>

  <div v-if="last_generated_report" class="card shadow-sm border rounded-lg p-3 mb-4">
    <p class="fw-bold text-dark text-xxs mb-1">{{ last_generated_report.name }}</p>
    <p class="text-muted text-xxs mb-2">
      Status: {{ last_generated_report.status?.display_name || "Completed" }}
    </p>
    <div class="d-flex gap-2">
      <a
        v-if="last_generated_report.presigned_url"
        :href="last_generated_report.presigned_url"
        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
        rel="noopener noreferrer"
        target="_blank"
      >
        Download report
      </a>
      <span v-else-if="last_generated_report.error_message" class="text-danger text-xxs">
        {{ last_generated_report.error_message }}
      </span>
    </div>
  </div>

  <div class="rounded-lg p-2">
    <ServerPaginatedTable
        :headers="template_table_headers"
        :data="table_safe_templates"
        :loading="loading_templates"
        :next_page="next_page"
        :page_number="page_number"
        :page_size="page_size"
        :prev_page="prev_page"
        :total_count="total_count"
        :total_pages="total_pages"
        :empty_message="translate_label('No saved templates found.')"
        :loading_text="translate_label('Loading templates...')"
        table_id="report_template_library_table"
        @page-change="go_to_page"
    >
      <template #actions="{ row }">
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          data-bs-toggle="modal"
          data-bs-target="#report_template_details"
          type="button"
          @click="open_template_details(row)"
        >
          View
        </button>
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          type="button"
          @click="edit_template(row)"
        >
          Edit
        </button>
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          :disabled="generating_template_id === row.id || !selected_output_format_id"
          type="button"
          @click="quick_generate(row)"
        >
          <span
            v-if="generating_template_id === row.id"
            class="spinner-border spinner-border-sm me-2"
          ></span>
          {{ generating_template_id === row.id ? "Generating..." : "Quick Generate" }}
        </button>
      </template>
    </ServerPaginatedTable>
  </div>

  <teleport to="body">
    <div class="modal fade" id="report_template_details" tabindex="-1" aria-labelledby="report_template_details_label" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
          <div class="modal-header border-bottom-0 cute-header shadow-lg">
            <div>
              <p class="modal-title text-white text-xxs mb-1" id="report_template_details_label">
                {{ selected_template?.name || translate_label("Template Details") }}
              </p>
              <p class="text-white text-xxs mb-0">
                {{ selected_template?.report_type?.display_name || "-" }} | {{ selected_template?.scope_type?.display_name || "-" }}
              </p>
            </div>
            <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close" @click="close_template_details"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3 mb-4">
              <div class="col-12 col-md-6">
                <div class="card shadow-sm border rounded-lg p-3 h-100">
                  <p class="fw-bold text-xxs mb-1">{{ translate_label("Description") }}</p>
                  <p class="text-muted text-xxs mb-0">{{ selected_template?.description || translate_label("No description added.") }}</p>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="card shadow-sm border rounded-lg p-3 h-100">
                  <p class="fw-bold text-xxs mb-1">{{ translate_label("Entity Types") }}</p>
                  <p class="text-muted text-xxs mb-0">{{ selected_template ? format_entity_types(selected_template) : "-" }}</p>
                </div>
              </div>
            </div>

            <div class="rounded-lg p-2 mb-4">
              <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Columns") }}</p>
              <ServerPaginatedTable
                :headers="detail_column_headers"
                :data="detail_columns"
                :show_pagination="false"
                table_id="report_template_columns_table"
              />
            </div>

            <div class="rounded-lg p-2 mb-4">
              <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Question Scope And Answer Filters") }}</p>
              <ServerPaginatedTable
                :headers="detail_question_filter_headers"
                :data="detail_question_filters"
                :show_pagination="false"
                table_id="report_template_questions_table"
              />
            </div>

            <div v-if="detail_question_metrics.length" class="rounded-lg p-2 mb-4">
              <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Question Statistics") }}</p>
              <ServerPaginatedTable
                :headers="detail_question_metric_headers"
                :data="detail_question_metrics"
                :show_pagination="false"
                table_id="report_template_question_metrics_table"
              />
            </div>

            <div v-if="detail_other_filters.length" class="rounded-lg p-2 mb-4">
              <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Other Filters") }}</p>
              <ServerPaginatedTable
                :headers="detail_other_filter_headers"
                :data="detail_other_filters"
                :show_pagination="false"
                table_id="report_template_other_filters_table"
              />
            </div>

            <div v-if="detail_entity_types.length" class="rounded-lg p-2">
              <p class="fw-bold text-dark text-xxs px-2 pt-2 mb-2">{{ translate_label("Assigned Entity Types") }}</p>
              <ServerPaginatedTable
                :headers="detail_entity_type_headers"
                :data="detail_entity_types"
                :show_pagination="false"
                table_id="report_template_entity_types_table"
              />
            </div>
          </div>
          <div class="modal-footer border-0 p-2 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              data-bs-dismiss="modal"
              @click="close_template_details"
            >
              {{ translate_label("Close") }}
            </button>
            <button
              type="button"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              data-bs-dismiss="modal"
              @click="selected_template && edit_template(selected_template)"
            >
              {{ translate_label("Edit In Builder") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
  </div>
</div>
